import { useMsal } from '@azure/msal-react'
import { ThemeToggle } from '../ui/ThemeToggle'

interface TopBarProps {
  onMenuClick: () => void
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const { accounts, instance } = useMsal()
  const account = accounts[0]

  const handleSignOut = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: '/' })
  }

  const initials = account?.name
    ? account.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : '?'

  return (
    <header className="topbar">
      {/* Hamburger — visible only on mobile */}
      <button
        className="topbar-menu"
        onClick={onMenuClick}
        aria-label="Toggle navigation menu"
      >
        <span className="topbar-menu-bar" />
        <span className="topbar-menu-bar" />
        <span className="topbar-menu-bar" />
      </button>

      <div className="topbar-spacer" />

      <div className="topbar-right">
        <ThemeToggle />
        {account && (
          <>
            <div className="topbar-user">
              <div className="topbar-avatar">{initials}</div>
              <div className="topbar-user-info">
                <span className="topbar-user-name">{account.name ?? account.username}</span>
                <span className="topbar-user-email">{account.username}</span>
              </div>
            </div>
            <button className="topbar-signout" onClick={handleSignOut} title="Sign out">
              ⎋ Sign out
            </button>
          </>
        )}
      </div>

      <style>{`
        .topbar {
          height: var(--topbar-height);
          background: var(--bg-surface);
          border-bottom: 1px solid var(--border);
          display: flex;
          align-items: center;
          padding: 0 24px;
          position: sticky;
          top: 0;
          z-index: 10;
          gap: 12px;
        }
        .topbar-menu {
          display: none;
          flex-direction: column;
          justify-content: center;
          gap: 4px;
          width: 36px;
          height: 36px;
          padding: 6px 7px;
          border-radius: var(--radius-sm);
          transition: background 0.15s;
          flex-shrink: 0;
        }
        .topbar-menu:hover { background: var(--bg-hover); }
        .topbar-menu-bar {
          display: block;
          width: 100%;
          height: 2px;
          background: var(--text-primary);
          border-radius: 1px;
          transition: background 0.15s;
        }
        .topbar-spacer { flex: 1; }
        .topbar-right {
          display: flex;
          align-items: center;
          gap: 16px;
          min-width: 0;
        }
        .topbar-user {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }
        .topbar-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--accent);
          color: #fff;
          font-size: 0.75rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .topbar-user-info {
          display: flex;
          flex-direction: column;
          gap: 1px;
          min-width: 0;
        }
        .topbar-user-name {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--text-primary);
          line-height: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .topbar-user-email {
          font-size: 0.7rem;
          color: var(--text-muted);
          line-height: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .topbar-signout {
          font-size: 0.78rem;
          color: var(--text-muted);
          padding: 6px 12px;
          border-radius: var(--radius-sm);
          border: 1px solid var(--border);
          transition: color 0.15s, border-color 0.15s;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .topbar-signout:hover {
          color: var(--color-error);
          border-color: var(--color-error);
        }

        /* ── Mobile ── */
        @media (max-width: 768px) {
          .topbar { padding: 0 16px; }
          .topbar-menu { display: flex; }
          .topbar-user-info { display: none; }
          .topbar-signout { padding: 6px 10px; font-size: 0.75rem; }
        }
        @media (max-width: 420px) {
          .topbar-signout span { display: none; }
        }
      `}</style>
    </header>
  )
}
